import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { useIsMobile } from '../layout'
import { usePageTitle } from '../title'
import { CONFLICTS, NOT_ADVICE } from '../components/Disclosures'

// How calls are found and scored. Keep this in step with src/extract/ and
// src/api/scorecard.ts: it is the page every performance number links to.

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="method-sec">
      <h2>{title}</h2>
      {children}
    </section>
  )
}

export function MethodologyBody() {
  return (
    <div className="method" data-testid="methodology">
      <Section title="Where the calls come from">
        <p>
          Each new episode of a tracked podcast is transcribed by speech-to-text. An AI model (Claude) then reads the
          transcript and lists the US-listed stocks discussed, deciding for each whether the speaker made a buy, sell or
          hold call, or no call at all, and how strongly (conviction, 1 to 5). ADRs count; OTC and non-US listings
          aren’t covered yet.
        </p>
        <p>
          The text under “Why this pick” is the model’s summary of the argument, not a quote. Extraction can be wrong:
          it may misread a ticker, attribute a guest’s view to the host, or take commentary for a recommendation. Every
          call links to the audio clip it came from, and the clip is the source of truth.
        </p>
        <p>By default only buy, sell and hold calls with conviction 3 or higher are shown and scored.</p>
      </Section>

      <Section title="How a pick is scored">
        <ul>
          <li>
            <strong>Entry</strong> is the opening price of the first US trading session that starts at or after the
            episode’s publish time, so no pick is scored at a price a listener couldn’t have got.
          </li>
          <li>
            <strong>Return</strong> runs from that open to the latest daily close. A sell call gains when the stock
            falls. Hold calls are shown but not scored.
          </li>
          <li>
            <strong>Win rate</strong> is the share of scored buy and sell calls with a positive return so far.
          </li>
          <li>
            Returns are <strong>price-only</strong>: they leave out dividends, trading costs and slippage. Prices are
            split-adjusted when they’re fetched.
          </li>
        </ul>
      </Section>

      <Section title="If you’d followed every pick">
        <p>
          This curve is <strong>hypothetical</strong>. No one traded it. Every scored pick gets an equal share from its
          entry and is never sold, and the curve is the average return of the picks held at each date.
        </p>
        <p>
          The dashed SPY line puts the same money into the S&amp;P 500 ETF on the same entry days, including for sell
          calls. The gap between the two lines is how far following the show was ahead of, or behind, simply buying the
          market.
        </p>
      </Section>

      <Section title="Known limitations">
        <ul>
          <li>Tracking started recently, so most podcasts have few scored picks and the numbers are mostly noise.</li>
          <li>A stock that stops trading keeps its last price, which can hide a loss after a delisting.</li>
          <li>A stock recommended again in a later episode counts as a new pick each time.</li>
          <li>Ticker changes after a merger or rename can cut a stock’s price history short.</li>
        </ul>
      </Section>

      <Section title="Independence">
        <p>{CONFLICTS}</p>
        <p>
          {NOT_ADVICE} PodcastAlpha reports what podcasts said and how those calls have done. Past performance doesn’t
          predict future results.
        </p>
      </Section>
    </div>
  )
}

export function Methodology() {
  const mobile = useIsMobile()
  usePageTitle('Methodology')
  return mobile ? (
    <div>
      <h1 className="m-h1">Methodology</h1>
      <p className="m-sub">How calls are found and scored</p>
      <MethodologyBody />
    </div>
  ) : (
    <div className="list-col">
      <div className="crumbs" style={{ marginBottom: 16 }}>
        <Link to="/">Home</Link>
        <span className="sep" aria-hidden>/</span>
        <span className="now">Methodology</span>
      </div>
      <h1 className="page-title">Methodology</h1>
      <p className="page-sub">How calls are found and scored</p>
      <MethodologyBody />
    </div>
  )
}
